import React from 'react';
import { trackTikTokLead, trackTikTokInitiateCheckout } from '@/services/analytics';
import { useButtonTracking } from '@/shared/components/usePageTracking';

export function Hero() {
  const { trackClick } = useButtonTracking();

  const handleHeroCTA = () => {
    trackClick('Join Waitlist - $19 Early Bird', 'Hero Section', {
      section: 'hero_cta',
      page: 'landing'
    });
    
    // Track TikTok lead and initiate checkout
    trackTikTokLead('audit_interest', 'hero_button');
    trackTikTokInitiateCheckout('audit_report_19', 19, 'USD');
    
    // Redirect to authentication page
    window.location.href = '/authenticate';
  };

  return (
    <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden min-h-screen flex items-center pt-8 sm:pt-12 md:pt-0">
      {/* Simplified Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-purple-600/10 rounded-full mix-blend-multiply filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-orange-600/10 rounded-full mix-blend-multiply filter blur-3xl"></div>
      </div>
      
      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Column - Content */}
          <div className="space-y-12">
            {/* Main Headline */}
            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.1] tracking-tight">
                <span className="block text-white mb-3">Find Out Why Your AI-Built App Has Zero Users</span>
                <span className="block bg-gradient-to-r from-orange-400 via-red-400 to-orange-500 bg-clip-text text-transparent">
                  And How to Fix It
                </span>
              </h1>
            </div>
            
            {/* CTA Section */}
            <div className="space-y-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative inline-block">
                  <div className="absolute -top-3 -right-3 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg z-10">
                    Early Access
                  </div>
                  <button 
                    onClick={handleHeroCTA}
                    className="group relative bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold text-xl px-12 py-6 rounded-2xl hover:from-orange-700 hover:to-red-700 transform hover:scale-[1.02] transition-all duration-300 shadow-2xl border border-orange-400/20 cursor-pointer"
                  >
                    <span className="relative z-10 flex items-center gap-3">
                      Join Waitlist - $19 Early Bird
                      <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
              
              {/* Platform Support */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <p className="text-slate-300 text-sm font-medium mb-4">Will connect to your code from:</p>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="bg-white/10 text-slate-200 px-4 py-2 rounded-xl border border-white/10 text-sm font-medium hover:bg-white/15 transition-colors">Lovable</span>
                  <span className="bg-white/10 text-slate-200 px-4 py-2 rounded-xl border border-white/10 text-sm font-medium hover:bg-white/15 transition-colors">Cursor</span>
                  <span className="bg-white/10 text-slate-200 px-4 py-2 rounded-xl border border-white/10 text-sm font-medium hover:bg-white/15 transition-colors">v0</span>
                  <span className="bg-white/10 text-slate-200 px-4 py-2 rounded-xl border border-white/10 text-sm font-medium hover:bg-white/15 transition-colors">Bolt</span>
                  <span className="bg-white/10 text-slate-200 px-4 py-2 rounded-xl border border-white/10 text-sm font-medium hover:bg-white/15 transition-colors">Claude</span>
                  <span className="text-slate-400 text-sm font-medium">+ any GitHub repo</span>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed">All AI coding tools can publish to GitHub - we analyze directly from your repo</p>
              </div>
            </div>
          </div>
          
          {/* Right Column - Audit Report Mockup */}
          <div className="relative lg:block hidden">
            <div className="relative">
              {/* Single Accent Element */}
              <div className="absolute -top-8 -right-8 w-16 h-16 bg-gradient-to-br from-orange-400 to-red-400 rounded-2xl rotate-12 opacity-20"></div>
              
              {/* Main Mockup Container */}
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <div className="ml-4 text-slate-300 text-sm font-mono">Growth Audit Report</div>
                  </div>
                  
                  {/* Audit Report Content */}
                  <div className="space-y-4">
                    <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                      <div className="mb-5 pb-4 border-b border-white/10">
                        <div className="text-orange-300 font-bold text-lg mb-1 flex items-center gap-2">
                          Growth Score: 1/5 
                          <span className="text-xl">⭐</span>
                        </div>
                      </div>
                      <h3 className="text-white font-bold mb-5 text-lg">App Growth Analysis</h3>
                      
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                          <div className="w-5 h-5 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-bold">✗</span>
                          </div>
                          <span className="text-red-300 font-medium">Analytics: Not Found</span>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                          <div className="w-5 h-5 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-bold">✗</span>
                          </div>
                          <span className="text-red-300 font-medium">Email Capture: Missing</span>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                          <div className="w-5 h-5 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-bold">⚠</span>
                          </div>
                          <span className="text-yellow-300 font-medium">Customer Support: Basic setup found</span>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                          <div className="w-5 h-5 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-bold">✗</span>
                          </div>
                          <span className="text-red-300 font-medium">Marketing Pixels: Not Found</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-orange-600/15 border border-orange-500/30 rounded-2xl p-4">
                      <h4 className="text-orange-300 font-bold text-sm mb-2 flex items-center gap-2">
                        🔥 Priority #1
                      </h4>
                      <p className="text-slate-200 text-sm leading-relaxed">Add analytics first - you're flying blind without user data.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
}